import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import PublicModal from 'components/PublicModal'
import LogoLink from 'components/logoLink'
import Button from 'components/Button'
import Link from 'components/Link'
import { ArrowLeftIcon, ArrowRightIcon, FacebookIcon, GoogleStyledIcon } from 'components/icons'
import { useMutation, useQuery } from 'react-query'
import ApiClient from 'data/api/api_client'
import queryCache from 'data/queryCache'
import Form from 'components/form'
import Checkbox from 'components/form/Checkbox'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { setUser } from 'store/auth'
import CustomerRegisterModal from 'components/modals/CustomerRegisterModal'

const CenteredCustomerLoginModal = ({ modalOpen, onClose }) => {
  const dispatch = useDispatch()
  const handleError = useErrorHandler()

  const { user } = useSelector(state => ({
    user: state.auth.user,
  }))

  const [registerModalOpen, setRegisterModalOpen] = useState(false)

  const [loginWithEmail, setLoginWithEmail] = useState(false)

  const { data: socialLinks } = useQuery(
    queryCache.socialLinks,
    () => ApiClient.auth.getSocialLinks(),
  )

  const { register, handleSubmit, getValues, setError, formState: { errors } } = useForm()

  const loginMutation = useMutation(
    () => ApiClient.auth.login(getValues('email'), getValues('password'), getValues('remember_me')),
    {
      onSuccess: (user) => {
        dispatch(setUser(user))
      },
      onError: (error) => {
        handleError(error, setError)
      },
    },
  )

  const closeModal = () => {
    setLoginWithEmail(false)
    onClose()
  }

  useEffect(() => {
    if (user) {
      onClose()
    }
  }, [user])

  return (
    <PublicModal
      isOpen={modalOpen}
      onClose={() => closeModal()}
      className={`max-w-[452px] ${registerModalOpen ? 'hidden' : ''}`}
      innerPadding="!px-20 !pb-40 !pt-40 lg:!px-40 lg:!pt-50 lg:!pb-70"
    >
      <div className="flex flex-col items-center gap-30">
        <LogoLink className="w-[130px] focus:outline-none" />
        <div className="flex flex-col gap-10 text-center">
          {
            loginWithEmail
              ? <p className="font-bold text-grey-900 text-16">Sign into your account</p>
              : <>
                <p className="font-bold text-grey-900 text-16">Please sign in to access this feature</p>
                <h5 className="text-grey-500 text-14">
                  To access this feature you must have an account with us.<br />
                  Simply log in below or sign up now; <br />
                  this will only take 10 seconds.
                </h5>
              </>
          }
        </div>
        {
          loginWithEmail
            ?            <div className="w-full mt-50">
              <form onSubmit={handleSubmit(loginMutation.mutate)} className="flex flex-col gap-20">
                <Form.Label className="!text-14 text-grey-800">
                  Email Address
                  <Form.Input
                    style="customer"
                    className="mt-[9px] !ring-home-divider h-60"
                    placeholder="Your email here"
                    error={errors?.email?.message}
                    {...register('email')}
                  />
                </Form.Label>

                <Form.Label className="!text-14 text-grey-800">
                  Password
                  <Form.Input
                    style="customer"
                    className="mt-[9px] !ring-home-divider h-60"
                    placeholder="Enter your password"
                    type="password"
                    error={errors?.password?.message}
                    {...register('password')}
                  />
                </Form.Label>

                <Button type="submit" block className="mt-10 uppercase rounded-4" isLoading={loginMutation.isLoading}>Login</Button>

                <div className="flex items-center justify-between w-full mt-10 whitespace-nowrap text-grey-800">
                  <Checkbox
                    style="customer"
                    error={errors?.remember_me?.message}
                    {...register('remember_me')}
                  >
                    Remember me
                  </Checkbox>

                  <Link className="text-14 text-grey-500" href="/forgotten-password">
                    Forgot Password
                  </Link>
                </div>
              </form>

              <button type="button" className="absolute cursor-pointer top-20 left-20" onClick={() => setLoginWithEmail(false)}>
                <ArrowLeftIcon />
              </button>
            </div>
            : <div className="flex flex-col w-full gap-20 mt-15">
              <Link className="no-underline" href={socialLinks?.facebook ?? '#'}>
                <Button
                  block={true}
                  variant="outline-grey"
                  className="flex items-center justify-between group hover:bg-white hover:!border-primary px-16 py-8 h-[52px] !font-roboto"
                >
                  <div className="flex items-center">
                    <FacebookIcon className="text-20 text-[#1877F2]" />
                    <span className="text-14 ml-[32px] text-grey-800 normal-case leading-20 !font-medium">Sign in with Facebook</span>
                  </div>
                  <ArrowRightIcon className="text-grey-800 text-opacity-40 group-hover:text-opacity-100" />
                </Button>
              </Link>

              <Link className="no-underline" href={socialLinks?.google ?? '#'}>
                <Button
                  block={true}
                  variant="outline-grey"
                  className="flex items-center justify-between group hover:bg-white hover:!border-primary px-16 py-8 h-[52px] !font-roboto"
                >
                  <div className="flex items-center">
                    <GoogleStyledIcon className="text-20" />
                    <span className="text-14 ml-[32px] text-grey-800 normal-case leading-20 !font-medium">Sign in with Google</span>
                  </div>
                  <ArrowRightIcon className="text-grey-800 text-opacity-40 group-hover:text-opacity-100" />
                </Button>
              </Link>
              <div className="flex gap-15">
                <hr className="w-full my-auto bg-grey-800 opacity-40" />
                <p className="text-grey-800 text-14">or</p>
                <hr className="w-full my-auto bg-grey-800 opacity-40" />
              </div>

              <Button className="font-bold uppercase text-18 rounded-4" onClick={() => setLoginWithEmail(true)}>Log in with email</Button>

              <p className="mt-10 text-center text-grey-500 text-14">
                {'Don\'t have an account yet? '}
                <button className="ml-20 underline text-primary" onClick={() => setRegisterModalOpen(true)}>
                  Sign up now
                </button>
              </p>

              <button type="button" className="absolute top-20 left-20" onClick={() => closeModal()}>
                <ArrowLeftIcon />
              </button>
            </div>
        }
      </div>

      <CustomerRegisterModal
        isOpen={registerModalOpen}
        onClose={() => {
          setRegisterModalOpen(false)
          if (user) {
            onClose()
          }
        }}
      />
    </PublicModal>
  )
}

CenteredCustomerLoginModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CenteredCustomerLoginModal
